import React, { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CreditCard, Wallet, Building2, Heart, Briefcase, Users, TrendingUp, Loader2 } from 'lucide-react';
import { stripeService } from '../services/stripeService';
import { paypalService } from '../services/paypalService';
export function DonatePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    amount: '',
    currency: 'EUR'
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  const paypalContainerRef = useRef<HTMLDivElement>(null);
  // Load PayPal hosted button when PayPal is selected
  useEffect(() => {
    if (paymentMethod === 'paypal' && !paypalReady) {
      setPaypalLoading(true);
      paypalService.renderHostedButton('paypal-button-container').then(success => {
        setPaypalLoading(false);
        setPaypalReady(success);
        if (!success) {
          console.log('PayPal hosted button not available, will use redirect fallback');
        }
      });
    }
  }, [paymentMethod]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Manual validation with clear messages
    if (!formData.firstName.trim()) {
      alert('Veuillez entrer votre prénom.');
      return;
    }
    if (!formData.lastName.trim()) {
      alert('Veuillez entrer votre nom.');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      alert('Veuillez entrer une adresse email valide.');
      return;
    }
    if (!formData.amount) {
      alert('Veuillez entrer un montant.');
      return;
    }
    if (!paymentMethod) {
      alert('Veuillez sélectionner un moyen de paiement.');
      return;
    }
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Veuillez entrer un montant valide.');
      return;
    }
    setIsProcessing(true);
    const donationData = {
      amount,
      currency: formData.currency.toLowerCase(),
      donorName: `${formData.firstName} ${formData.lastName}`,
      donorEmail: formData.email,
      companyName: formData.companyName
    };
    try {
      let result;
      switch (paymentMethod) {
        case 'stripe':
          // Stripe redirects the page, so no need to handle result
          result = await stripeService.createCheckoutSession(donationData);
          if (!result.success) {
            alert(result.error || 'Une erreur est survenue avec Stripe.');
          }
          break;
        case 'paypal':
          // If PayPal hosted button is rendered, user clicks it directly
          if (paypalReady) {
            alert('Veuillez utiliser le bouton PayPal affiché ci-dessus.');
            setIsProcessing(false);
            return;
          }
          // Fallback: redirect to PayPal
          result = await paypalService.createPayment(donationData);
          if (!result.success) {
            alert(result.error || 'Une erreur est survenue avec PayPal.');
          }
          break;
        default:
          alert('Méthode de paiement non supportée.');
          break;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Une erreur est survenue lors du traitement du paiement.');
    } finally {
      setIsProcessing(false);
    }
  };
  return <Layout data-id="element-840">
      <SEO {...pageSEO.donate} data-id="element-841" />
      <div className="bg-[#2C3E50] text-white py-16" data-id="element-842">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-843">
          <h1 className="text-4xl font-bold mb-4" data-id="element-844">Faire un don</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-6" data-id="element-845">
            Votre soutien permet non seulement de former, mais aussi de financer
            des dispositifs de placement, d'accompagner les bénéficiaires vers
            un emploi durable, et de structurer un modèle d'insertion
            responsable.
          </p>
          <div className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3" data-id="element-846">
            <p className="text-lg font-semibold" data-id="element-847">
              Former sans débouché n'est pas suffisant. Soutenez l'insertion
              professionnelle durable.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-848">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-849">
          <Card className="bg-blue-50 border-blue-100" data-id="element-850">
            <CardContent className="p-6 text-center" data-id="element-851">
              <Users className="w-8 h-8 text-[#1E88E5] mx-auto mb-4" data-id="element-852" />
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-853">Former</h3>
              <p className="text-sm text-gray-600" data-id="element-854">
                Financer des formations professionnelles certifiantes de
                qualité.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-100" data-id="element-855">
            <CardContent className="p-6 text-center" data-id="element-856">
              <Briefcase className="w-8 h-8 text-[#1E88E5] mx-auto mb-4" data-id="element-857" />
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-858">Placer</h3>
              <p className="text-sm text-gray-600" data-id="element-859">
                Soutenir les dispositifs de placement et mise en relation avec
                employeurs.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-100" data-id="element-860">
            <CardContent className="p-6 text-center" data-id="element-861">
              <TrendingUp className="w-8 h-8 text-[#1E88E5] mx-auto mb-4" data-id="element-862" />
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-863">Suivre</h3>
              <p className="text-sm text-gray-600" data-id="element-864">
                Accompagner les bénéficiaires après leur placement pour garantir
                la durabilité.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Impact Statement */}
        <div className="bg-gradient-to-r from-[#1E88E5] to-[#1976D2] text-white rounded-xl p-8 mb-12 text-center" data-id="element-865">
          <h2 className="text-2xl font-bold mb-4" data-id="element-866">
            Votre don finance l'insertion professionnelle complète
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6" data-id="element-867">
            <div data-id="element-868">
              <p className="text-3xl font-bold mb-2" data-id="element-869">50€</p>
              <p className="text-sm text-blue-100" data-id="element-870">
                Finance 1 mois de suivi post-placement
              </p>
            </div>
            <div data-id="element-871">
              <p className="text-3xl font-bold mb-2" data-id="element-872">150€</p>
              <p className="text-sm text-blue-100" data-id="element-873">
                Forme et place 1 bénéficiaire
              </p>
            </div>
            <div data-id="element-874">
              <p className="text-3xl font-bold mb-2" data-id="element-875">500€</p>
              <p className="text-sm text-blue-100" data-id="element-876">
                Soutient le dispositif de placement pendant 1 mois
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-877">
          <div className="lg:col-span-2" data-id="element-878">
            <Card data-id="element-879">
              <CardContent className="p-8" data-id="element-880">
                <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-881">
                  Informations du donateur
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-882">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-883">
                    <div data-id="element-884">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-885">
                        Prénom *
                      </label>
                      <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-886" />
                    </div>
                    <div data-id="element-887">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-888">
                        Nom *
                      </label>
                      <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-889" />
                    </div>
                  </div>

                  <div data-id="element-890">
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-891">
                      Nom de l'entreprise (optionnel)
                    </label>
                    <input type="text" id="companyName" name="companyName" value={formData.companyName} onChange={handleInputChange} disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" placeholder="Si vous représentez une entreprise" data-id="element-892" />
                  </div>

                  <div data-id="element-893">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-894">
                      Email *
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-895" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-896">
                    <div data-id="element-897">
                      <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-898">
                        Montant du don *
                      </label>
                      <input type="number" id="amount" name="amount" value={formData.amount} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" placeholder="0.00" min="1" step="0.01" data-id="element-899" />
                    </div>
                    <div data-id="element-900">
                      <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-901">
                        Devise *
                      </label>
                      <select id="currency" name="currency" value={formData.currency} onChange={handleInputChange} disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border bg-white disabled:opacity-50" data-id="element-902">
                        <option value="EUR" data-id="element-903">Euro (€)</option>
                        <option value="USD" data-id="element-904">Dollar ($)</option>
                        <option value="XOF" data-id="element-905">Franc CFA (XOF)</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-100" data-id="element-906">
                    <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-907">
                      Moyen de paiement
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-908">
                      <button type="button" onClick={() => {
                      setPaymentMethod('stripe');
                      setPaypalReady(false);
                    }} disabled={isProcessing} className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-all disabled:opacity-50 ${paymentMethod === 'stripe' ? 'border-[#1E88E5] bg-blue-50 ring-2 ring-[#1E88E5] ring-opacity-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-909">
                        <CreditCard className="w-6 h-6 text-[#635BFF] mb-2" data-id="element-910" />
                        <span className="font-bold text-[#635BFF]" data-id="element-911">
                          Carte bancaire
                        </span>
                        <span className="text-xs text-gray-500 mt-1" data-id="element-912">
                          via Stripe
                        </span>
                      </button>
                      <button type="button" onClick={() => setPaymentMethod('paypal')} disabled={isProcessing} className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-all disabled:opacity-50 ${paymentMethod === 'paypal' ? 'border-[#1E88E5] bg-blue-50 ring-2 ring-[#1E88E5] ring-opacity-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-913">
                        <Wallet className="w-6 h-6 text-[#003087] mb-2" data-id="element-914" />
                        <span className="font-bold text-[#003087]" data-id="element-915">PayPal</span>
                        <span className="text-xs text-gray-500 mt-1" data-id="element-916">
                          Compte PayPal
                        </span>
                      </button>
                    </div>

                    {/* PayPal Hosted Button Container */}
                    {paymentMethod === 'paypal' && <div className="mb-6" data-id="element-917">
                        {paypalLoading && <div className="flex items-center justify-center gap-2 py-6 text-gray-500" data-id="element-918">
                            <Loader2 className="w-5 h-5 animate-spin" data-id="element-919" />
                            <span className="text-sm" data-id="element-920">
                              Chargement de PayPal...
                            </span>
                          </div>}
                        <div id="paypal-button-container" ref={paypalContainerRef} className={`min-h-[50px] ${paypalLoading ? 'hidden' : ''}`} data-id="element-921" />
                        {!paypalLoading && !paypalReady && <p className="text-xs text-gray-500 text-center mt-2" data-id="element-922">
                            Cliquez sur "Procéder au paiement" pour être
                            redirigé vers PayPal.
                          </p>}
                      </div>}

                    {/* Show submit button for Stripe, or as fallback for PayPal */}
                    {(paymentMethod === 'stripe' || paymentMethod === 'paypal' && !paypalReady || !paymentMethod) && <Button type="submit" className="w-full" size="lg" disabled={isProcessing} data-id="element-923">
                        {isProcessing ? <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-924" />
                            Redirection en cours...
                          </> : !paymentMethod ? 'Sélectionnez un moyen de paiement' : paymentMethod === 'stripe' ? '💳 Payer par carte bancaire' : 'Procéder au paiement PayPal'}
                      </Button>}
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-6" data-id="element-925">
            <Card className="bg-gray-50 border-gray-200" data-id="element-926">
              <CardContent className="p-6" data-id="element-927">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-928">
                  <Heart className="w-5 h-5 text-[#1E88E5]" data-id="element-929" />
                  Impact Direct
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-930">
                  100% de votre don finance directement :
                </p>
                <ul className="space-y-3 text-sm text-gray-700" data-id="element-931">
                  <li className="flex items-start gap-2" data-id="element-932">
                    <span className="text-[#1E88E5] font-bold" data-id="element-933">•</span>
                    <span data-id="element-934">Les formations professionnelles</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-935">
                    <span className="text-[#1E88E5] font-bold" data-id="element-936">•</span>
                    <span data-id="element-937">Les dispositifs de placement</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-938">
                    <span className="text-[#1E88E5] font-bold" data-id="element-939">•</span>
                    <span data-id="element-940">L'accompagnement post-insertion</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-941">
                    <span className="text-[#1E88E5] font-bold" data-id="element-942">•</span>
                    <span data-id="element-943">Le suivi et la médiation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gray-50 border-gray-200" data-id="element-944">
              <CardContent className="p-6" data-id="element-945">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-946">
                  <CreditCard className="w-5 h-5 text-[#1E88E5]" data-id="element-947" />
                  Virement Bancaire
                </h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center" data-id="element-948">
                  <p className="text-amber-800 font-medium text-sm mb-1" data-id="element-949">
                    🏗️ Bientôt disponible
                  </p>
                  <p className="text-amber-700 text-xs" data-id="element-950">
                    Le paiement par virement bancaire sera disponible
                    prochainement. En attendant, utilisez la carte bancaire ou
                    PayPal.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}